import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Testimonials: React.FC = () => {
  const testimonials = [
    {
      name: 'John Smith',
      role: 'Amateur Angler',
      content: 'An incredible experience! The guides were knowledgeable and made sure we had a great time. The scenery was breathtaking, and the fishing spots were perfect.',
      rating: 5,
      image: '/images/fishing2.jpg'
    },
    {
      name: 'Sarah Johnson',
      role: 'Professional Fisher',
      content: 'The equipment provided was top-notch, and the locations were perfect for fishing. I\'ve been fishing for years, and this was one of the best experiences I\'ve had.',
      rating: 5,
      image: '/images/fishing3.jpg'
    },
    {
      name: 'Michael Brown',
      role: 'First-time Fisher',
      content: 'As a beginner, I was nervous, but the team made me feel comfortable and taught me so much. The patience and expertise of the guides were outstanding.',
      rating: 4,
      image: '/images/fishing5.jpg'
    },
    {
      name: 'Emma Wilson',
      role: 'Family Trip',
      content: 'We brought our children for their first fishing experience, and it was perfect. The guides were great with kids and made it an educational and fun day.',
      rating: 5,
      image: '/images/fishing7.jpg'
    },
    {
      name: 'David Thompson',
      role: 'Regular Customer',
      content: 'I\'ve been coming here for years, and the service just keeps getting better. The new equipment and knowledgeable staff make every trip special.',
      rating: 5,
      image: '/images/fishing8.jpg'
    },
    {
      name: 'Lisa Anderson',
      role: 'Corporate Event',
      content: 'We organised a team-building day, and it was a huge success. The staff handled everything perfectly, and everyone had a fantastic time.',
      rating: 5,
      image: '/images/fishing10.jpg'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Testimonials - Anchor Management</title>
        <meta name="description" content="Read what our customers say about their experiences with Anchor Management at Loch Lomond" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/scenery 1.jpg"
            alt="Loch Lomond fishing scene"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold mb-4"
          >
            Customer Stories
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl max-w-2xl mx-auto"
          >
            Discover what our customers say about their fishing adventures
          </motion.p>
        </div>
        
        {/* Wave Shape */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg
            className="w-full h-auto"
            viewBox="0 0 1440 320"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#00B4D8"
              fillOpacity="0.8"
              d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,261.3C960,256,1056,224,1152,208C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            ></path>
          </svg>
        </div>
      </section>

      {/* Loch Lomond Stories Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div className="aspect-w-4 aspect-h-3 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/scenery2.jpg"
                  alt="Loch Lomond fishing experience"
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h2 className="text-4xl font-bold mb-6 text-gray-800 dark:text-white">
                Unforgettable Moments at Loch Lomond
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
                Over the years, we've had the privilege of creating countless magical moments at Loch Lomond. From the early morning mist rising over the water to the golden sunsets casting long shadows across the mountains, every day brings new adventures and stories to tell.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
                One of our most memorable experiences was with a family who had never fished before. The joy on their faces when they caught their first trout was priceless. The father, who was initially hesitant, ended up being the most enthusiastic angler of the day, proudly showing off his catch to everyone at the lodge.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-8">
                Another highlight was hosting a group of international visitors who had travelled specifically to experience Scottish fishing. Their amazement at the beauty of Loch Lomond and the abundance of wildlife made us realise once again how lucky we are to call this place home.
              </p>
              <Link
                to="/gallery"
                className="inline-block bg-gradient-to-r from-primary-600 to-primary-500 text-white px-8 py-4 rounded-full font-semibold hover:from-primary-700 hover:to-primary-600 transition-all transform hover:scale-105 shadow-lg"
              >
                View Our Gallery
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="relative h-48">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-yellow-500">
                    {'★'.repeat(testimonial.rating)}
                    {'☆'.repeat(5 - testimonial.rating)}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2 text-gray-800 dark:text-white">{testimonial.name}</h3>
                  <p className="text-primary-600 dark:text-primary-400 mb-4">{testimonial.role}</p>
                  <p className="text-gray-700 dark:text-gray-300 italic">"{testimonial.content}"</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-8 text-gray-800 dark:text-white">Share Your Experience</h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            Have you enjoyed a fishing adventure with us? We'd love to hear your story and share it with our community.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-primary-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-primary-700 transition-colors"
          >
            Write a Review
          </motion.button>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4 text-gray-800 dark:text-white">Subscribe to Our Newsletter</h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            Stay updated with our latest fishing adventures, special offers, and tips from our expert guides.
          </p>
          <motion.form 
            className="max-w-md mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-3 rounded-full border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:border-primary-500 dark:focus:border-primary-400"
                required
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-primary-600 to-primary-500 text-white px-8 py-3 rounded-full font-semibold hover:from-primary-700 hover:to-primary-600 transition-all transform hover:scale-105 shadow-lg"
                type="submit"
              >
                Subscribe
              </motion.button>
            </div>
          </motion.form>
        </div>
      </section>
    </div>
  );
};

export default Testimonials; 